import { useEffect, type RefObject } from 'react';
import './useMiddleButtonPanScroll.css';

const BASE_CLASS = 'table-middle-pan';
const ACTIVE_CLASS = 'is-middle-panning';

/* Для `a` средняя кнопка остаётся для браузера (открытие в новой вкладке). */
const SKIP_INTERACTIVE =
  'a, input, textarea, select, label, [contenteditable="true"], [role="slider"]';

/** Pan скролл-контейнера зажатой средней кнопкой мыши (как «рука» по полотну). */
export function useMiddleButtonPanScroll(
  scrollRef: RefObject<HTMLElement | null>,
  ready: boolean
): void {
  useEffect(() => {
    if (!ready) return;
    const el = scrollRef.current;
    if (!el) return;

    el.classList.add(BASE_CLASS);

    let panning = false;
    let activePointerId = -1;
    let lastX = 0;
    let lastY = 0;

    const removeDocListeners = () => {
      document.removeEventListener('pointermove', onDocMove, true);
      document.removeEventListener('pointerup', onDocEnd, true);
      document.removeEventListener('pointercancel', onDocEnd, true);
    };

    const stopPan = (opts?: { releasePointerId?: number }) => {
      if (!panning) return;
      panning = false;
      const pid = opts?.releasePointerId ?? activePointerId;
      activePointerId = -1;
      el.classList.remove(ACTIVE_CLASS);
      removeDocListeners();
      if (pid >= 0) {
        try {
          el.releasePointerCapture(pid);
        } catch {
          /* already released */
        }
      }
    };

    const onDocMove = (e: PointerEvent) => {
      if (!panning || e.pointerId !== activePointerId) return;
      e.preventDefault();
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      el.scrollLeft -= dx;
      el.scrollTop -= dy;
    };

    const onDocEnd = (e: PointerEvent) => {
      if (!panning || e.pointerId !== activePointerId) return;
      if (e.type === 'pointerup' && e.button !== 1) return;
      stopPan({ releasePointerId: e.pointerId });
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 1) return;
      const t = e.target;
      if (!(t instanceof Node) || !el.contains(t)) return;
      if (t instanceof Element && t.closest(SKIP_INTERACTIVE)) return;

      e.preventDefault();
      panning = true;
      activePointerId = e.pointerId;
      lastX = e.clientX;
      lastY = e.clientY;
      el.classList.add(ACTIVE_CLASS);
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* продолжаем только с document listeners */
      }
      document.addEventListener('pointermove', onDocMove, { capture: true, passive: false });
      document.addEventListener('pointerup', onDocEnd, { capture: true });
      document.addEventListener('pointercancel', onDocEnd, { capture: true });
    };

    const onLostCapture = (e: PointerEvent) => {
      if (!panning || e.pointerId !== activePointerId) return;
      panning = false;
      activePointerId = -1;
      el.classList.remove(ACTIVE_CLASS);
      removeDocListeners();
    };

    el.addEventListener('pointerdown', onPointerDown, { passive: false });
    el.addEventListener('lostpointercapture', onLostCapture);

    return () => {
      stopPan({ releasePointerId: activePointerId });
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('lostpointercapture', onLostCapture);
      removeDocListeners();
      el.classList.remove(BASE_CLASS, ACTIVE_CLASS);
    };
  }, [ready, scrollRef]);
}
